/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { forwardRef, useCallback, useEffect, useLayoutEffect } from 'react';

import { css, jsx } from '@compiled/react';

import type { IframePassThroughProps } from './IframeElementType';
import { useIframeCommunicationChannel } from './useIframeCommunicationChannel';
import { useHostOffsetOverride } from './useHostOffsetOverride';

import { usePerformanceMetrics } from '../performance-metrics';
import { useUniqueKey } from '../unique-key-provider/UniqueKeyProvider';

const iframeStyles = css({
	border: 'none',
	width: '100%',
	height: '100%',
});

export type IframeProps = {
	onContentLoadStarted?: () => void;
	onLoad?: React.ReactEventHandler<HTMLIFrameElement>;
	onUnload?: () => void;
	src: string;
	style?: React.CSSProperties;
} & IframePassThroughProps;

export const Iframe: React.ForwardRefExoticComponent<
	IframeProps & React.RefAttributes<HTMLIFrameElement>
> = forwardRef<HTMLIFrameElement, IframeProps>(
	({ src, onLoad, onUnload, onContentLoadStarted, ...passThroughProps }, ref) => {
		const { markIframeLoadMetrics } = usePerformanceMetrics();
		useLayoutEffect(() => {
			markIframeLoadMetrics('common-load-end');
			markIframeLoadMetrics('iframe-content-initialized-start');
		}, [markIframeLoadMetrics]);
		const iframeOnLoad = useCallback(
			(event: React.SyntheticEvent<HTMLIFrameElement>) => {
				onLoad && onLoad(event);
			},
			[onLoad],
		);
		const { uniqueKey } = useUniqueKey();

		useEffect(() => {
			const handler = (e: MessageEvent) => {
				// only fire onUnload handler if uniqueKey (if it exists) matches
				const uniqueKeyMatch = !uniqueKey || !e.data?.uniqueKey || e.data.uniqueKey === uniqueKey;
				if (e.data.type === 'windowUnload' && uniqueKeyMatch) {
					onUnload?.();
				}
			};
			window.addEventListener('message', handler);

			return () => {
				window.removeEventListener('message', handler);
			};
		}, [onUnload, uniqueKey]);
		useHostOffsetOverride();

		const channel = useIframeCommunicationChannel(src, {
			...passThroughProps,
			onContentLoadStarted,
		});

		// implementation because cleaner index export is not possible with forwardRef
		return (
			<iframe
				ref={ref}
				css={iframeStyles}
				title="confluence-article-iframe"
				data-testid="confluence-page-iframe"
				name={channel.name}
				src={src}
				onLoad={iframeOnLoad}
				// Grant features to iframe (Currently the same as smart card iframe's granted features)
				allow="autoplay; fullscreen; encrypted-media; clipboard-write"
				// Same as "fullscreen" in "allow" attribute but it's considered a legacy attribute. https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe#attr-allowfullscreen
				// Need it in case the browser doesn't support "fullscreen" feature policy.
				allowFullScreen
				{...passThroughProps}
			/>
		);
	},
);
